/**
 * ActionCheckbox - Checkbox for action selection with disabled state styling
 *
 * Uses the UntitledUI Checkbox component for consistent styling.
 * Supports disabled state with tooltip for showing reason.
 */

import { Checkbox } from '@/lib/base/forms/checkbox';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { cx } from '@/utils/cx';

export interface ActionCheckboxProps {
  /** Whether the checkbox is checked */
  checked: boolean;
  /** Whether the checkbox is disabled (e.g., due to dependency) */
  disabled: boolean;
  /** Callback when checkbox is toggled */
  onChange: () => void;
  /** Reason for disabled state (shown in tooltip) */
  disabledReason?: string;
  /** ARIA label for accessibility */
  ariaLabel?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Action checkbox with optional tooltip for disabled state
 *
 * @example
 * ```tsx
 * <ActionCheckbox
 *   checked={isSelected(action.id)}
 *   disabled={isDisabled(action.id)}
 *   onChange={() => toggleAction(action.id)}
 *   disabledReason={getDisabledReason(action.id)}
 *   ariaLabel={`Select action ${action.id}`}
 * />
 * ```
 */
export function ActionCheckbox({
  checked,
  disabled,
  onChange,
  disabledReason,
  ariaLabel,
  className,
}: ActionCheckboxProps) {
  const checkbox = (
    <Checkbox
      isSelected={checked}
      isDisabled={disabled}
      onChange={onChange}
      aria-label={ariaLabel}
      className={cx(className)}
    />
  );

  // Wrap in tooltip if disabled with a reason
  if (disabled && disabledReason) {
    return (
      <Tooltip title={disabledReason} placement='top'>
        <TooltipTrigger>
          <span className='inline-flex'>{checkbox}</span>
        </TooltipTrigger>
      </Tooltip>
    );
  }

  return checkbox;
}
